import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService, { initProactiveAccessTokenRefresh } from "../../utils/apiService";
import { setLocalStorage } from '../../utils/localStorage';
import { processPendingReferral } from '../../utils/referralTracking';
import { getLocationForAuth } from '../../utils/geoLocation';

// Async thunks for OTP API calls
export const sendOTP = createAsyncThunk(
  "otp/sendOTP",
  async ({ email, username, platform, device }, { rejectWithValue }) => {
    try {
      const response = await apiService.post("/auth/send-otp", {
        email,
        username,
        platform: platform || 'web',
        device: device || 'web',
      }, { requiresAuth: false });
      return response.data;
    } catch (error) {
      // Check if error has validation details
      if (error.data?.details && Array.isArray(error.data.details)) {
        // Extract and join all validation messages
        const validationMessages = error.data.details.map(detail => detail.message).join(', ');
        return rejectWithValue(validationMessages);
      }
      return rejectWithValue(error.message || "Failed to send OTP");
    }
  }
);

export const verifyOTP = createAsyncThunk(
  "otp/verifyOTP",
  async ({ email, username, password, otp, sessionId, fcmToken, device, platform, signupPage }, { rejectWithValue }) => {
    try {
      // Get pending tokens from session storage
      const pendingTokens = JSON.parse(sessionStorage.getItem('pending_tokens') || '[]');
      
      const location = getLocationForAuth();
      const response = await apiService.post("/auth/verify-otp", {
        email,
        username,
        password,
        otp,
        sessionId,
        fcmToken,
        device,
        platform,
        signupPage,
        ...(location && { location }),
        ...(pendingTokens.length > 0 && { pendingTokenAddresses: pendingTokens }),
      }, { requiresAuth: false });
      
      // If registration was successful (tokens returned), store user data
      if (response.data.accessToken || response.data.token) {
        setLocalStorage('autobot_user', JSON.stringify(response.data));
        if (typeof window !== 'undefined') initProactiveAccessTokenRefresh();
        // Clear pending tokens from session storage
        if (pendingTokens.length > 0) {
          sessionStorage.removeItem('pending_tokens');
        }
      }
      
      return response.data;
    } catch (error) {
      // Check if error has validation details
      if (error.data?.details && Array.isArray(error.data.details)) {
        // Extract and join all validation messages
        const validationMessages = error.data.details.map(detail => detail.message).join(', ');
        return rejectWithValue(validationMessages);
      }
      return rejectWithValue(error.message || "Failed to verify OTP");
    }
  }
);


const initialState = {
  isLoading: false,
  isOtpSent: false,
  isOtpVerified: false,
  error: null,
  message: null,
  otp: "",
  email: "",
  username: "",
};

const otpSlice = createSlice({
  name: "otp",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    clearAll: (state) => {
      state.error = null;
      state.message = null;
      state.isOtpSent = false;
      state.isOtpVerified = false;
      state.otp = "";
      state.email = "";
      state.username = "";
    },
    setOtp: (state, action) => {
      state.otp = action.payload;
    },
    setEmail: (state, action) => {
      state.email = action.payload;
    },
    setUsername: (state, action) => {
      state.username = action.payload;
    },
    resetOtpState: (state) => {
      state.isOtpSent = false;
      state.isOtpVerified = false;
      state.otp = "";
    },
  },
  extraReducers: (builder) => {
    // Send OTP
    builder
      .addCase(sendOTP.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(sendOTP.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isOtpSent = true;
        state.message = action.payload.message || "OTP sent successfully";
      })
      .addCase(sendOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Verify OTP
    builder
      .addCase(verifyOTP.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isOtpVerified = true;
        state.message = action.payload.message || "OTP verified successfully";
        
        // Process pending referral code after successful registration (when tokens are present)
        if (action.payload?.accessToken || action.payload?.token) {
          processPendingReferral().catch(err => {
            console.error('Failed to process referral after signup:', err);
          });
        }
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

  },
});

export const { 
  clearError, 
  clearMessage, 
  clearAll, 
  setOtp, 
  setEmail, 
  setUsername,
  resetOtpState 
} = otpSlice.actions;

export default otpSlice.reducer; 